import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import { axiosApi } from "../../../interceptors/axios";
import { Grid2 } from "@mui/material";

// TypeScript için interface tanımlamaları
interface AreaGradientProps {
  color: string;
  id: string;
}

// API'den gelen veri yapısı (tarih dizisi)
type StatisticData = string[];

interface SeriesItem {
  id: string;
  label: string;
  showMark: boolean;
  curve: "linear" | "natural" | "step" | "bump" | "monotone";
  data: number[];
  area: boolean;
}

interface ProcessedData {
  xAxisData: string[];
  count: number[];
  datesByDay: Record<string, number>;
  totalSessions: number;
  percentageChange: number;
}

// Alan için gradient oluşturan bileşen
function AreaGradient({ color, id }: AreaGradientProps) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

// Tarih verilerini işleyen yardımcı fonksiyon
function processDateData(dates: string[]): ProcessedData {
  // Tarihleri dayjs nesnelerine dönüştürme
  const dayjsDates = dates.map((dateStr) => dayjs(dateStr));

  // Tarihleri günlere göre gruplandırma
  const datesByDay: Record<string, number> = {};

  dayjsDates.forEach((date) => {
    const dayKey = date.format("YYYY-MM-DD");
    datesByDay[dayKey] = (datesByDay[dayKey] || 0) + 1;
  });

  // Son 30 günü kapsayacak şekilde tüm günleri oluştur
  const today = dayjs();
  const thirtyDaysAgo = today.subtract(29, "day");
  const allDays: string[] = [];
  const counts: number[] = [];

  for (let i = 0; i < 30; i++) {
    const currentDay = thirtyDaysAgo.add(i, "day");
    const dayKey = currentDay.format("YYYY-MM-DD");
    const formattedDay = currentDay.format("DD MMM");

    allDays.push(formattedDay);
    counts.push(datesByDay[dayKey] || 0);
  }

  // Toplam oturum sayısı
  const totalSessions = dates.length;

  // Son 7 gün ve önceki 7 günün verileri
  const last7DaysData = counts.slice(-7).reduce((acc, curr) => acc + curr, 0);
  const previous7DaysData = counts
    .slice(-14, -7)
    .reduce((acc, curr) => acc + curr, 0);

  // Yüzde değişimi hesaplama
  let percentageChange = 0;
  if (previous7DaysData > 0) {
    percentageChange = Math.round(
      ((last7DaysData - previous7DaysData) / previous7DaysData) * 100
    );
  }

  return {
    xAxisData: allDays,
    count: counts,
    datesByDay,
    totalSessions,
    percentageChange,
  };
}

export default function SessionsChart(): JSX.Element {
  const theme = useTheme();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [statisticData, setStatisticData] =
    React.useState<StatisticData | null>(null);
  const [processedData, setProcessedData] =
    React.useState<ProcessedData | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Renk paleti
  const colorPalette: string[] = [
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
  ];

  // API'den veri çekme
  React.useEffect(() => {
    const fetchStatistics = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await axiosApi.get<StatisticData>(
          "api/users/statistic"
        );
        setStatisticData(response.data);

        // Tarih verilerini işle
        const processed = processDateData(response.data);
        setProcessedData(processed);

        setLoading(false);
      } catch (err) {
        console.error("İstatistik verileri alınırken hata:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Veriler yüklenirken bir hata oluştu"
        );
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // Yükleme durumu
  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veriler yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography color="error">Hata: {error}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Veriler yoksa
  if (!processedData) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veri bulunamadı</Typography>
        </CardContent>
      </Card>
    );
  }

  // Veri serilerini oluştur
  const generateSeries = (): SeriesItem[] => {
    return [
      {
        id: "sessions",
        label: "Kayıt oluşturma",
        showMark: false,
        curve: "linear",
        data: processedData.count,
        area: true,
      },
    ];
  };

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Grid2 container>
          <Grid2 size={{ xs: 12 }}>
            <Stack
              direction="row"
              sx={{
                alignContent: { xs: "center", sm: "flex-start" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h4" component="p">
                {processedData.totalSessions.toLocaleString("tr-TR")}
              </Typography>
              <Chip
                size="small"
                color={
                  processedData.percentageChange >= 0 ? "success" : "error"
                }
                label={`${processedData.percentageChange >= 0 ? "+" : ""}${
                  processedData.percentageChange
                }%`}
              />
            </Stack>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Typography component="h2" variant="subtitle2" gutterBottom>
              Kayıt oluşturma
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Son 30 gün için günlük oturumlar
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <LineChart
              colors={[theme.palette.primary.main]}
              xAxis={[
                {
                  scaleType: "point",
                  data: processedData.xAxisData,
                  tickInterval: (index: number) => index % 5 === 0,
                },
              ]}
              series={generateSeries() as any}
              height={250}
              grid={{ horizontal: true }}
              sx={{
                "& .MuiAreaElement-series-sessions": {
                  fill: "url('#sessions')",
                },
              }}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}
            >
              <AreaGradient color={theme.palette.primary.main} id="sessions" />
            </LineChart>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
}
